import type { ReactNode } from 'react';

import { ApiError } from '@mds/shared/lib/api';
import { Alert } from '@mds/shared/ui/feedback/alert';
import { Button } from '@mds/shared/ui/shadcn/button';
import { Skeleton } from '@mds/shared/ui/shadcn/skeleton';

/**
 * The three things a panel can be while it waits on the API, in one place so
 * every screen says them the same way.
 */

/** Placeholder rows at roughly the height of what is coming. */
export function LoadingBlock({ rows = 3 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-3" aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-24 w-full rounded-card" />
      ))}
    </div>
  );
}

/** No answer we can read: offline, the API restarting, a gateway's own error page. */
const UNREACHABLE = "We couldn't reach MDS just now. Check your connection and try again in a minute.";

/**
 * Codes any screen can meet, in the visitor's words. A page with something
 * better to say (a field to point at) handles the code itself first.
 */
const BY_CODE: Partial<Record<string, string>> = {
  RATE_LIMITED: 'Too many tries in a short time. Wait a minute, then try again.',
  VALIDATION_ERROR: 'Some details need another look. Check the form and try again.',
  CONTACT_NOT_CONFIGURED: "Messages can't be sent right now. Call or WhatsApp us instead.",
  PROPOSAL_FACILITY_UNAVAILABLE: "This space stopped taking proposals. Contact MDS and we'll help.",
  FACILITY_PROPOSAL_REQUIRED: 'This space is booked by proposal. Send one from its page.',
  EVENT_FEEDBACK_NOT_FOUND:
    "This feedback link doesn't open a form any more. Open it again from your email.",
};

/**
 * What a failure says on screen.
 *
 * The server's own `message` only for a 4xx it answered in JSON: a 5xx is
 * never the visitor's to read, and a reply that is not our JSON (a proxy's
 * 502 page) has no message at all, only the fetch wrapper's "Request failed
 * (502)". The API writes its messages without a closing full stop; one is
 * added so they read like the rest of the site.
 */
function errorText(error: unknown): string {
  if (!(error instanceof ApiError)) return UNREACHABLE;
  const known = error.code ? BY_CODE[error.code] : undefined;
  if (known) return known;
  if (error.status >= 500 || error.body === null) return UNREACHABLE;
  return /[.!?]$/.test(error.message) ? error.message : `${error.message}.`;
}

/**
 * A failure, in words the visitor can act on: the page's own `message` when it
 * has one, else `errorText(error)`.
 */
export function ErrorBlock({
  title,
  error,
  message,
  onDismiss,
  onRetry,
  action,
}: {
  title: string;
  error: unknown;
  /** Says it better than the server could, for a code this page knows. */
  message?: string;
  /** Makes it closable — for a failed action, not for a page that could not load. */
  onDismiss?: () => void;
  /** Adds a Retry; for a read, which a second try can fix. */
  onRetry?: () => void;
  /** The one thing to do about it, when it is not Retry. */
  action?: ReactNode;
}) {
  // The shared alert, so a failure reads like every other message on the site.
  return (
    <Alert
      tone="danger"
      title={title}
      onDismiss={onDismiss}
      action={
        action ??
        (onRetry && (
          <Button variant="link" className="h-auto p-0" onClick={onRetry}>
            Retry
          </Button>
        ))
      }
    >
      {message ?? errorText(error)}
    </Alert>
  );
}

/** Nothing to show, and why — never a bare blank panel. */
export function EmptyBlock({ title, hint }: { title: string; hint?: ReactNode }) {
  return (
    <div className="rounded-card border border-dashed p-10 text-center">
      <p className="m-0 font-medium">{title}</p>
      {hint && <p className="mt-1 mb-0 text-sm text-muted-foreground">{hint}</p>}
    </div>
  );
}
